import React from 'react';
import { api, Mark, MarkMember, MarkMeatSharingItem, MarkMeatQueueItem, MarkViltRegistration, ViltHandlingLog, FallAvgift, KottKostnad, MarkShootingPlanItem, MarkRolePermissions } from '../api.ts';
import { LabelPrinter, LabelData as LabelDataType, generateLabels as generateLabelsFn, DEFAULT_VILT_CUTS } from '../LabelPrinter';
import { FAMILY_DISPLAY_NAMES as WD_FL } from '../wildlifeData.ts';
import { Beef, History, Minus, Plus, Printer, Tags, Trash2 } from 'lucide-react';
import { fmtD } from '../components';
import { RADIUS, TYPE, useTheme, type Theme } from '../theme';
import { Badge, Button, Card, Dialog, EmptyState, Field, Icon, IconButton, Input, Label, Loader, SectionHeader, Select, Switch, Textarea, type BadgeTone } from '../components/ui';

interface KottdelningTabProps {
  mark: Mark;
  meatLd: boolean;
  members: MarkMember[];
  viltRegs: MarkViltRegistration[];
  setViltRegs: React.Dispatch<React.SetStateAction<MarkViltRegistration[]>>;
  falltViltPins: any[];
  setFalltViltPins: React.Dispatch<React.SetStateAction<any[]>>;
  meatItems: MarkMeatSharingItem[];
  setMeatItems: React.Dispatch<React.SetStateAction<MarkMeatSharingItem[]>>;
  queue: MarkMeatQueueItem[];
  plan: MarkShootingPlanItem[];
  fallavgifter: FallAvgift[];
  kottkostnad: KottKostnad[];
  isAdmin?: boolean;
  myPerms: MarkRolePermissions | null;
  // Vilt disposition modal state
  viltDispModal: {reg:MarkViltRegistration;step:'choose'|'list_share'|'for_sale'|'to_shooter'}|null;
  setViltDispModal: React.Dispatch<React.SetStateAction<{reg:MarkViltRegistration;step:'choose'|'list_share'|'for_sale'|'to_shooter'}|null>>;
  viltShareCount: number;
  setViltShareCount: React.Dispatch<React.SetStateAction<number>>;
  viltAvpalsat: boolean;
  setViltAvpalsat: (v:boolean) => void;
  viltSlaktvikt: string;
  setViltSlaktvikt: (v:string) => void;
  viltTrikin: boolean;
  setViltTrikin: (v:boolean) => void;
  viltCesium: boolean;
  setViltCesium: (v:boolean) => void;
  viltSaleNotes: string;
  setViltSaleNotes: (v:string) => void;
  viltFallavgift: string;
  setViltFallavgift: (v:string) => void;
  viltKottkostnad: string;
  setViltKottkostnad: (v:string) => void;
  viltVikt: string;
  setViltVikt: (v:string) => void;
  viltSaving: boolean;
  setViltSaving: (v:boolean) => void;
  showAddVilt: boolean;
  setShowAddVilt: (v:boolean) => void;
  addViltForm: {gameType:string;shotDate:string;shotByName:string;shotByEmail:string;sex:string;ageClass:string;estimatedWeightKg:string;notes:string};
  setAddViltForm: React.Dispatch<React.SetStateAction<{gameType:string;shotDate:string;shotByName:string;shotByEmail:string;sex:string;ageClass:string;estimatedWeightKg:string;notes:string}>>;
  handlingLogModal: MarkViltRegistration|null;
  setHandlingLogModal: (v:MarkViltRegistration|null) => void;
  handlingLog: ViltHandlingLog[];
  setHandlingLog: React.Dispatch<React.SetStateAction<ViltHandlingLog[]>>;
  handlingLogLd: boolean;
  setHandlingLogLd: (v:boolean) => void;
  showLabelPrinter: boolean;
  setShowLabelPrinter: (v:boolean) => void;
  labelPrintReg: MarkViltRegistration|null;
  setLabelPrintReg: (v:MarkViltRegistration|null) => void;
  user: any;
}

export function KottdelningTab({
  mark, meatLd, members, viltRegs, setViltRegs, falltViltPins, setFalltViltPins,
  meatItems, setMeatItems, queue, plan, fallavgifter, kottkostnad,
  isAdmin, myPerms,
  viltDispModal, setViltDispModal,
  viltShareCount, setViltShareCount,
  viltAvpalsat, setViltAvpalsat,
  viltSlaktvikt, setViltSlaktvikt,
  viltTrikin, setViltTrikin,
  viltCesium, setViltCesium,
  viltSaleNotes, setViltSaleNotes,
  viltFallavgift, setViltFallavgift,
  viltKottkostnad, setViltKottkostnad,
  viltVikt, setViltVikt,
  viltSaving, setViltSaving,
  showAddVilt, setShowAddVilt,
  addViltForm, setAddViltForm,
  handlingLogModal, setHandlingLogModal,
  handlingLog, setHandlingLog,
  handlingLogLd, setHandlingLogLd,
  showLabelPrinter, setShowLabelPrinter,
  labelPrintReg, setLabelPrintReg,
  user,
}: KottdelningTabProps) {
  const T = useTheme();
  if (meatLd) return <Loader text="Laddar viltkött…" />;

  const MEAT_SPECIES=['Vildsvin','Rådjur','Dovhjort','Kronhjort','Älg','Mufflon'];
  const season=new Date().getMonth()>=6?`${new Date().getFullYear()}/${new Date().getFullYear()+1}`:`${new Date().getFullYear()-1}/${new Date().getFullYear()}`;
  const activeMembers=(members.filter(m=>['jaktledare','vice_jaktledare','admin','hundforare','jagare','eftersoksjagare'].includes((m.role||'').toLowerCase().replace(/\s/g,'_').replace(/ä/g,'a').replace(/ö/g,'o')))).length>0?members.filter(m=>['jaktledare','vice_jaktledare','admin','hundforare','jagare','eftersoksjagare'].includes((m.role||'').toLowerCase().replace(/\s/g,'_').replace(/ä/g,'a').replace(/ö/g,'o'))):members;
  const canHandle=!!(isAdmin||myPerms?.canManageShootingPlan);

  /* ── Hjälpfunktioner ── */
  const dispLabel=(d:string)=>{switch(d){case'to_list':return'Köttlista';case'to_shooter':return'Skytten (helt)';case'shooter_portion':return'Skytten (lott)';case'to_landowner':return'Markägare';case'for_sale':return'Sälj';default:return'Ej hanterat';}};
  const dispTone=(d:string):BadgeTone=>{switch(d){case'to_list':return'ok';case'to_shooter':case'shooter_portion':return'info';case'for_sale':return'accent';default:return'neutral';}};

  /* ── fallt vilt pins ej registrerade ── */
  const confirmedPinIds=new Set(viltRegs.map(r=>r.falltViltPinId).filter(Boolean));
  const unregisteredPins=falltViltPins.filter(p=>!confirmedPinIds.has(p.id)&&p.viltslag);

  /* ── Köttfördelningsmatris ── */
  const matrixAnimals=viltRegs.filter(r=>r.disposition==='to_list'&&MEAT_SPECIES.includes(r.gameType));
  const matrixCols=matrixAnimals;

  const HANDLING_STEPS:string[]=['påskjutet','död','upphämtad','urtagen','avpälsad','hängd','styckad'];
  const handlingLabel=(s:string)=>({påskjutet:'Påskjutet',död:'Död',upphämtad:'Upphämtad',urtagen:'Urtagen',avpälsad:'Avpälsad',hängd:'Hängd',styckad:'Styckad'}[s]||s);
  const resetAddForm=()=>setAddViltForm({gameType:'Älg',shotDate:new Date().toISOString().slice(0,10),shotByName:'',shotByEmail:'',sex:'',ageClass:'',estimatedWeightKg:'',notes:''});
  const saveAddVilt=async()=>{
    setViltSaving(true);
    try{
      const reg=await api.addViltRegistration({markId:mark.id,season,gameType:addViltForm.gameType,shotDate:addViltForm.shotDate||undefined,shotByName:addViltForm.shotByName||undefined,sex:addViltForm.sex||undefined,ageClass:addViltForm.ageClass||undefined,estimatedWeightKg:addViltForm.estimatedWeightKg?Number(addViltForm.estimatedWeightKg):undefined});
      setViltRegs(p=>[...p,reg]);
      setShowAddVilt(false);
      resetAddForm();
    }catch(e){alert('Kunde inte spara djuret.');}finally{setViltSaving(false);}
  };
  const setStatus=async(reg:MarkViltRegistration,s:string)=>{
    try{const log=await api.updateViltHandlingStatus(reg.id,s,undefined,localStorage.getItem('qvenari_user_name')||undefined);setViltRegs(p=>p.map(r=>r.id===reg.id?{...r,handlingStatus:log.newStatus as any}:r));}catch{alert('Kunde inte uppdatera status.');}
  };
  const grid2:React.CSSProperties={display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12};

  return (
    <div>
      <SectionHeader title="Viltkött" kicker={`Säsong ${season}`} action={
        <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'flex-end'}}>
          <Button variant="secondary" pill icon={<Icon as={Tags} size={18}/>} aria-pressed={showLabelPrinter} onClick={()=>{setShowLabelPrinter(!showLabelPrinter);setLabelPrintReg(null);}}>Etiketter</Button>
          <Button pill icon={<Icon as={Plus} size={18}/>} onClick={()=>setShowAddVilt(true)}>Lägg till vilt</Button>
        </div>
      }/>

      {/* ── Lägg till manuellt ── */}
      <Dialog open={showAddVilt} onClose={()=>setShowAddVilt(false)} title="Registrera nytt djur" actions={<>
        <Button variant="secondary" onClick={()=>setShowAddVilt(false)}>Avbryt</Button>
        <Button loading={viltSaving} onClick={saveAddVilt}>{viltSaving?'Sparar…':'Spara djur'}</Button>
      </>}>
        <div style={grid2}>
          <Field label="Viltslag *"><Select value={addViltForm.gameType} onChange={e=>setAddViltForm(p=>({...p,gameType:e.target.value}))}>
            {['Älg','Vildsvin','Dovhjort','Kronhjort','Rådjur','Mufflon','Fasan','Kanadagås','Björn','Lo','Räv','Hare'].map(s=><option key={s}>{s}</option>)}
          </Select></Field>
          <Field label="Datum"><Input type="date" value={addViltForm.shotDate} onChange={e=>setAddViltForm(p=>({...p,shotDate:e.target.value}))}/></Field>
          <Field label="Skytt (namn)"><Input type="text" value={addViltForm.shotByName} onChange={e=>setAddViltForm(p=>({...p,shotByName:e.target.value}))} placeholder="Namn"/></Field>
          <Field label="Kön"><Select value={addViltForm.sex} onChange={e=>setAddViltForm(p=>({...p,sex:e.target.value}))}>
            <option value="">Okänt</option><option value="hane">Hane</option><option value="hona">Hona</option>
          </Select></Field>
          <Field label="Åldersklass"><Select value={addViltForm.ageClass} onChange={e=>setAddViltForm(p=>({...p,ageClass:e.target.value}))}>
            <option value="">Okänt</option><option value="kalv">Kalv</option><option value="åring">Åring</option><option value="vuxen">Vuxen</option>
          </Select></Field>
          <Field label="Uppskattad vikt (kg)"><Input type="number" inputMode="decimal" value={addViltForm.estimatedWeightKg} onChange={e=>setAddViltForm(p=>({...p,estimatedWeightKg:e.target.value}))} placeholder="kg"/></Field>
        </div>
      </Dialog>

      {/* ── Oregistrerade fallt vilt pins ── */}
      {unregisteredPins.length>0&&(
        <div style={{marginBottom:24}}>
          <Label style={{marginBottom:8}}>Bekräftade från kartan – ej hanterade</Label>
          <Card padding={0}>
            {unregisteredPins.map((pin:any,i:number)=>(
              <div key={pin.id} style={{padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap',borderTop:i?`1px solid ${T.border}`:'none'}}>
                <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0}}>
                  <Badge tone="accent">{pin.viltslag}</Badge>
                  <div style={{minWidth:0}}>
                    <div style={{color:T.text,fontSize:15,fontWeight:600}}>{pin.name||pin.viltslag}</div>
                    <div style={{...TYPE.small,color:T.textMuted}}>{[pin.jagarEmail,pin.kon,pin.createdAt?fmtD(pin.createdAt):''].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
                <Button onClick={async()=>{
                  try{
                    const reg=await api.addViltRegistration({markId:mark.id,season,gameType:pin.viltslag,falltViltPinId:pin.id,shotDate:pin.createdAt?.slice(0,10),shotByEmail:pin.jagarEmail||undefined,sex:pin.kon||undefined,ageClass:pin.alder||undefined});
                    setViltRegs(p=>[...p,reg]);
                    setFalltViltPins(p=>p.filter(x=>x.id!==pin.id));
                  }catch{alert('Kunde inte registrera djuret.');}
                }}>Registrera</Button>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* ── Registrerade djur ── */}
      {viltRegs.length>0&&(
        <div style={{marginBottom:24}}>
          <Label style={{marginBottom:8}}>Registrerade djur</Label>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {viltRegs.map(reg=>{
              const kk=kottkostnad.find(k=>k.viltslag===reg.gameType);
              const fa=fallavgifter.find(f=>f.viltslag===reg.gameType)||(()=>{const pr=plan.find(r=>r.gameType===reg.gameType&&r.costPerAnimal!=null&&r.costPerAnimal>0);return pr?{viltslag:pr.gameType,kon:'',typ:'',kostnad:pr.costPerAnimal!} as FallAvgift:null;})();
              const hIdx=HANDLING_STEPS.indexOf(reg.handlingStatus||'påskjutet');
              const nextStatus=hIdx<HANDLING_STEPS.length-1?HANDLING_STEPS[hIdx+1]:null;
              const meta=[reg.shotDate?fmtD(reg.shotDate):'',reg.shotByName?`Skytt: ${reg.shotByName}`:'',reg.sex||'',reg.ageClass||'',reg.estimatedWeightKg?`~${reg.estimatedWeightKg} kg`:''].filter(Boolean).join(' · ');
              const toShooter=()=>{setViltDispModal({reg,step:'to_shooter'});setViltFallavgift(fa?String(fa.kostnad):'');setViltKottkostnad(kk?String(kk.kostnadPerKg):'');setViltVikt('');};
              return(
                <Card key={reg.id} padding={16}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:10}}>
                    <div style={{minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                        <span style={{...TYPE.title,fontSize:16,color:T.text}}>{reg.gameType} #{reg.animalNumber}</span>
                        {reg.djurId&&<Badge tone="accent" style={{fontFamily:'ui-monospace, monospace',letterSpacing:'0.06em'}}>{reg.djurId}</Badge>}
                        {reg.disposition!=='pending'&&<Badge tone={dispTone(reg.disposition)}>{dispLabel(reg.disposition)}</Badge>}
                      </div>
                      {meta&&<div style={{...TYPE.small,color:T.textMuted,marginTop:4,textTransform:'none'}}>{meta}</div>}
                    </div>
                    <div style={{display:'flex',alignItems:'center',flexShrink:0}}>
                      <IconButton label="Skriv ut etikett" onClick={()=>{setLabelPrintReg(reg);setShowLabelPrinter(true);setTimeout(()=>document.getElementById('label-printer-section')?.scrollIntoView({behavior:'smooth',block:'start'}),50);}}><Icon as={Printer} size={18}/></IconButton>
                      <IconButton label="Ta bort djuret" style={{color:T.danger}} onClick={async()=>{if(!confirm('Ta bort detta djur?'))return;await api.deleteViltRegistration(reg.id);setViltRegs(p=>p.filter(r=>r.id!==reg.id));setMeatItems(p=>p.filter(m=>!(m.gameType===reg.gameType&&m.animalNumber===reg.animalNumber)));}}><Icon as={Trash2} size={18}/></IconButton>
                    </div>
                  </div>

                  {/* Hanteringsstatus */}
                  <HandlingSteps T={T} steps={HANDLING_STEPS.map(handlingLabel)} current={hIdx}/>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',margin:'10px 0 4px'}}>
                    {nextStatus&&canHandle&&<Button variant="secondary" onClick={()=>setStatus(reg,nextStatus)}>→ {handlingLabel(nextStatus)}</Button>}
                    {hIdx>0&&canHandle&&(
                      <Select aria-label="Ändra status" defaultValue="" style={{width:'auto',minWidth:150}} onChange={async e=>{
                        const s=e.target.value;if(!s)return;
                        await setStatus(reg,s);
                        e.target.value='';
                      }}>
                        <option value="">Ändra status…</option>
                        {HANDLING_STEPS.map(s=><option key={s} value={s}>{handlingLabel(s)}</option>)}
                      </Select>
                    )}
                    <Button variant="ghost" icon={<Icon as={History} size={18}/>} onClick={async()=>{
                      setHandlingLogModal(reg);setHandlingLogLd(true);setHandlingLog([]);
                      try{const log=await api.viltHandlingLog(reg.id);setHandlingLog(log);}catch{}finally{setHandlingLogLd(false);}
                    }}>Historik</Button>
                  </div>

                  {reg.disposition==='for_sale'&&(
                    <div style={{display:'flex',gap:12,flexWrap:'wrap',fontSize:14,color:T.textSub,marginTop:6}}>
                      {reg.avpalsat&&<span>Avpälsad</span>}
                      {reg.slaktviktKg&&<span>Slaktvikt: {reg.slaktviktKg} kg</span>}
                      {reg.trikintestad&&<span style={{color:T.ok,fontWeight:600}}>Trikintestad</span>}
                      {reg.cesiumtestad&&<span style={{color:T.ok,fontWeight:600}}>Cesiumtestad</span>}
                      {reg.saleNotes&&<span style={{color:T.textMuted}}>{reg.saleNotes}</span>}
                    </div>
                  )}
                  {(reg.disposition==='to_shooter'||reg.disposition==='shooter_portion')&&(
                    <div style={{display:'flex',gap:12,flexWrap:'wrap',fontSize:14,color:T.textSub,marginTop:6}}>
                      {reg.fallavgiftKr!=null&&<span>Fällavgift: {reg.fallavgiftKr} kr</span>}
                      {reg.kottkostnadPerKg!=null&&reg.viktKg!=null&&<span style={{color:T.accentText,fontWeight:700}}>Kött att betala: {Math.round(reg.kottkostnadPerKg*reg.viktKg).toLocaleString()} kr ({reg.viktKg} kg × {reg.kottkostnadPerKg} kr/kg)</span>}
                    </div>
                  )}
                  {reg.disposition==='to_list'&&(
                    <div style={{fontSize:14,color:T.textSub,marginTop:6}}>Delas av {reg.shareCount} {reg.shareCount===1?'person':'personer'}</div>
                  )}

                  {reg.disposition==='pending'&&(
                    <div style={{borderTop:`1px solid ${T.border}`,marginTop:10,paddingTop:10}}>
                      <Label style={{marginBottom:6}}>Vart går köttet?</Label>
                      <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                        <Button variant="secondary" onClick={()=>{setViltDispModal({reg,step:'list_share'});setViltShareCount(2);}}>Köttlista</Button>
                        <Button variant="secondary" onClick={toShooter}>Hela till skytten</Button>
                        <Button variant="secondary" onClick={toShooter}>Köttlott till skytten</Button>
                        <Button variant="secondary" onClick={async()=>{
                          try{const updated=await api.updateViltDisposition(reg.id,{disposition:'to_landowner'});setViltRegs(p=>p.map(r=>r.id===reg.id?updated:r));}catch{alert('Kunde inte uppdatera.');}
                        }}>Markägare</Button>
                        <Button variant="secondary" onClick={()=>{setViltDispModal({reg,step:'for_sale'});setViltAvpalsat(false);setViltSlaktvikt('');setViltTrikin(false);setViltCesium(false);setViltSaleNotes('');}}>Annons</Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {viltRegs.length===0&&unregisteredPins.length===0&&(
        <Card style={{marginBottom:24}}>
          <EmptyState icon={Beef} title="Inga djur registrerade ännu" sub="Fällt vilt som bekräftas på kartan visas här automatiskt. Du kan också lägga till ett djur själv."
            action={<Button icon={<Icon as={Plus} size={18}/>} onClick={()=>setShowAddVilt(true)}>Lägg till vilt</Button>}/>
        </Card>
      )}

      {/* ── Köttfördelningsmatris ── */}
      {activeMembers.length>0&&matrixCols.length>0&&(
        <div style={{marginBottom:24}}>
          <Label style={{marginBottom:8}}>Köttfördelning</Label>
          <Card padding={0} style={{overflowX:'auto'}}>
            <table style={{borderCollapse:'collapse',fontSize:14,minWidth:'100%'}}>
              <thead>
                <tr>
                  <th style={{textAlign:'left',padding:'10px 14px',...TYPE.label,color:T.textMuted,borderBottom:`1px solid ${T.border}`,minWidth:140,position:'sticky',left:0,background:T.surface,zIndex:1}}>Medlem</th>
                  {matrixCols.map(animal=>(
                    <th key={animal.id} style={{textAlign:'center',padding:'10px',borderBottom:`1px solid ${T.border}`,minWidth:84,whiteSpace:'nowrap'}}>
                      <div style={{color:T.text,fontWeight:700,fontSize:14}}>{animal.gameType}</div>
                      <div style={{...TYPE.small,fontSize:12,color:T.textMuted}}>#{animal.animalNumber}{animal.shotDate?` · ${fmtD(animal.shotDate)}`:''}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeMembers.map((member,mi)=>{
                  const mName=member.name||member.userEmail;
                  const isNextInQueue=(animal:MarkViltRegistration)=>{
                    const shares=meatItems.filter(x=>x.gameType===animal.gameType&&x.animalNumber===animal.animalNumber);
                    return shares.some(s=>s.sharedToEmail===member.userEmail&&s.portionKg==null)||shares.some(s=>s.sharedToName===mName&&s.portionKg==null);
                  };
                  const hasReceived=(animal:MarkViltRegistration)=>{
                    const shares=meatItems.filter(x=>x.gameType===animal.gameType&&x.animalNumber===animal.animalNumber);
                    return shares.some(s=>(s.sharedToEmail===member.userEmail||s.sharedToName===mName)&&s.portionKg!=null);
                  };
                  const isQueueNext=queue.some(q=>q.memberEmail===member.userEmail&&q.queuePosition===1)||queue.filter(q=>q.memberEmail===member.userEmail).some(q=>q.queuePosition===Math.min(...queue.filter(x=>x.gameType===q.gameType).map(x=>x.queuePosition)));
                  return(
                    <tr key={member.id||member.userEmail} style={{borderTop:mi?`1px solid ${T.border}`:'none'}}>
                      <td style={{padding:'8px 14px',position:'sticky',left:0,background:T.surface,zIndex:1}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          {member.avatarUrl?<img src={member.avatarUrl.startsWith('http')?member.avatarUrl:`/uploads/${member.avatarUrl}`} style={{width:28,height:28,borderRadius:'50%',objectFit:'cover',flexShrink:0}} alt=""/>:<div aria-hidden style={{width:28,height:28,borderRadius:'50%',background:T.surface2,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:T.textSub,fontWeight:700,flexShrink:0}}>{(mName||'?')[0].toUpperCase()}</div>}
                          <span style={{color:T.text,fontWeight:isQueueNext?700:500}}>{mName}</span>
                          {isQueueNext&&<Badge tone="ok">Nästa</Badge>}
                        </div>
                      </td>
                      {matrixCols.map(animal=>{
                        const shareCount=animal.shareCount||1;
                        if(isNextInQueue(animal)) return <td key={animal.id} style={{textAlign:'center',padding:'8px 10px',background:T.okSoft}}><span style={{color:T.ok,fontWeight:800}}>1/{shareCount}</span></td>;
                        if(hasReceived(animal)) return <td key={animal.id} style={{textAlign:'center',padding:'8px 10px'}}><span style={{color:T.textMuted,fontWeight:600}}>1/{shareCount}</span></td>;
                        return <td key={animal.id} style={{textAlign:'center',padding:'8px 10px',color:T.textMuted}}>–</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ── Etikettsektionen ── */}
      {showLabelPrinter&&(()=>{
        const today=new Date().toISOString().slice(0,10);
        let preLabels:LabelDataType[]|undefined=undefined;
        if(labelPrintReg){
          const speciesLabel=WD_FL[labelPrintReg.gameType]||labelPrintReg.gameType;
          const weightKg=labelPrintReg.estimatedWeightKg!=null?String(labelPrintReg.estimatedWeightKg):'';
          const notes=[labelPrintReg.shotByName?`Skytt: ${labelPrintReg.shotByName}`:'',labelPrintReg.sex||'',labelPrintReg.ageClass||'',labelPrintReg.notes||''].filter(Boolean).join(' • ');
          const qrValue=labelPrintReg.djurId||labelPrintReg.id;
          const skjutetDate=labelPrintReg.shotDate||today;
          preLabels=generateLabelsFn(6,2,speciesLabel,'Innanlår',mark.name,DEFAULT_VILT_CUTS).map(l=>({
            ...l,
            skjutetDate,
            styckatDate:'',
            weightKg,
            qrValue,
            notes,
            viltRegId:labelPrintReg.id, // ET7: spårbarhet via etikettens QR-kod
          }));
        }
        return(
          <Card id="label-printer-section" padding={12} style={{marginBottom:24}}>
            <LabelPrinter key={labelPrintReg?labelPrintReg.id:'default'} user={user??null} isAdmin={!!isAdmin} defaultMarkName={mark.name} embedded
              preloadedLabels={preLabels}
              initialTemplateType={labelPrintReg?'vilt':undefined}
              onClose={()=>{setShowLabelPrinter(false);setLabelPrintReg(null);}}/>
          </Card>
        );
      })()}

      {/* ── Hanteringslogg ── */}
      <Dialog open={!!handlingLogModal} onClose={()=>setHandlingLogModal(null)} width={440}
        title={handlingLogModal&&<>{handlingLogModal.gameType} #{handlingLogModal.animalNumber}{handlingLogModal.djurId&&<span style={{...TYPE.small,color:T.textMuted,fontFamily:'ui-monospace, monospace',marginLeft:8}}>{handlingLogModal.djurId}</span>}</>}>
        <Label style={{marginBottom:10}}>Hanteringshistorik</Label>
        {handlingLogLd&&<Loader inline/>}
        {!handlingLogLd&&handlingLog.length===0&&<div style={{...TYPE.small,fontSize:14,color:T.textMuted}}>Ingen historik ännu.</div>}
        <div style={{display:'flex',flexDirection:'column'}}>
          {handlingLog.map((entry,i)=>(
            <div key={entry.id} style={{padding:'10px 0',borderTop:i?`1px solid ${T.border}`:'none'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,marginBottom:2}}>
                <span style={{color:T.text,fontWeight:700,fontSize:15}}>{entry.oldStatus?`${entry.oldStatus} → `:''}{entry.newStatus}</span>
                <span style={{...TYPE.small,color:T.textMuted}}>{new Date(entry.createdAt).toLocaleString('sv-SE',{dateStyle:'short',timeStyle:'short'})}</span>
              </div>
              <div style={{fontSize:14,color:T.textSub}}>{entry.changedByName||entry.changedByEmail||'Okänd'}{entry.comment?` – ${entry.comment}`:''}</div>
            </div>
          ))}
        </div>
      </Dialog>

      {/* ── Hantering av köttet ── */}
      {viltDispModal&&(()=>{
        const close=()=>setViltDispModal(null);
        const title=`${viltDispModal.reg.gameType} #${viltDispModal.reg.animalNumber}`;
        if(viltDispModal.step==='list_share') return(
          <Dialog open onClose={close} title={title} width={440} actions={<>
            <Button variant="secondary" onClick={close}>Avbryt</Button>
            <Button loading={viltSaving} onClick={async()=>{
              setViltSaving(true);
              const reg=viltDispModal.reg;
              const nextMembers=activeMembers.slice(0,viltShareCount);
              try{
                const shares=await api.distributeViltToList(reg.id,viltShareCount,nextMembers.map(m=>m.userEmail),nextMembers.map(m=>m.name||m.userEmail));
                const updated=await api.markViltRegistrations(mark.id);
                setViltRegs(updated);
                setMeatItems(p=>[...p,...shares]);
                setViltDispModal(null);
              }catch{alert('Kunde inte tilldela köttlistan.');}finally{setViltSaving(false);}
            }}>Tilldela och uppdatera kön</Button>
          </>}>
            <div style={{color:T.text,fontWeight:600,fontSize:15,marginBottom:12}}>Hur många ska dela djuret?</div>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
              <IconButton variant="filled" size={48} label="En person färre" onClick={()=>setViltShareCount(p=>Math.max(1,p-1))}><Icon as={Minus}/></IconButton>
              <span aria-live="polite" style={{color:T.text,fontWeight:800,fontSize:28,minWidth:40,textAlign:'center'}}>{viltShareCount}</span>
              <IconButton variant="filled" size={48} label="En person fler" onClick={()=>setViltShareCount(p=>Math.min(activeMembers.length||10,p+1))}><Icon as={Plus}/></IconButton>
              <span style={{color:T.textSub,fontSize:15}}>personer</span>
            </div>
            {activeMembers.length>0&&(
              <div>
                <Label style={{marginBottom:6}}>Nästa i tur</Label>
                {activeMembers.slice(0,viltShareCount).map((m,i)=>(
                  <div key={m.id||m.userEmail} style={{display:'flex',alignItems:'center',gap:10,minHeight:44,borderTop:i?`1px solid ${T.border}`:'none'}}>
                    <span style={{color:T.textMuted,fontWeight:700,fontSize:13,minWidth:24}}>#{i+1}</span>
                    <span style={{color:T.text,fontSize:15}}>{m.name||m.userEmail}</span>
                    <Badge tone="ok" style={{marginLeft:'auto'}}>1/{viltShareCount}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Dialog>
        );
        if(viltDispModal.step==='to_shooter') return(
          <Dialog open onClose={close} title={title} width={440} actions={<>
            <Button variant="secondary" onClick={close}>Avbryt</Button>
            <Button loading={viltSaving} onClick={async()=>{
              setViltSaving(true);
              try{
                const updated=await api.updateViltDisposition(viltDispModal.reg.id,{disposition:'to_shooter',fallavgiftKr:viltFallavgift?Number(viltFallavgift):undefined,kottkostnadPerKg:viltKottkostnad?Number(viltKottkostnad):undefined,viktKg:viltVikt?Number(viltVikt):undefined});
                setViltRegs(p=>p.map(r=>r.id===viltDispModal.reg.id?updated:r));
                setViltDispModal(null);
              }catch{alert('Kunde inte uppdatera.');}finally{setViltSaving(false);}
            }}>Spara</Button>
          </>}>
            <div style={{color:T.text,fontWeight:600,fontSize:15,marginBottom:12}}>Kostnad för skytten</div>
            <div style={{display:'grid',gap:12}}>
              <Field label="Fällavgift (kr)"><Input type="number" inputMode="numeric" value={viltFallavgift} onChange={e=>setViltFallavgift(e.target.value)} placeholder="0"/></Field>
              <Field label="Köttkostnad (kr/kg)"><Input type="number" inputMode="numeric" value={viltKottkostnad} onChange={e=>setViltKottkostnad(e.target.value)} placeholder="0"/></Field>
              {Number(viltKottkostnad)>0&&<Field label="Vikt (kg)"><Input type="number" inputMode="decimal" value={viltVikt} onChange={e=>setViltVikt(e.target.value)} placeholder="kg"/></Field>}
              {Number(viltKottkostnad)>0&&Number(viltVikt)>0&&(
                <div style={{background:T.accentSoft,borderRadius:RADIUS.md,padding:'12px 14px'}}>
                  <div style={{...TYPE.small,color:T.textSub}}>Skytten ska betala</div>
                  <div style={{color:T.text,fontWeight:800,fontSize:22,marginTop:2}}>
                    {(Number(viltFallavgift||0)+Number(viltKottkostnad)*Number(viltVikt)).toLocaleString()} kr
                  </div>
                  <div style={{...TYPE.small,color:T.textSub,marginTop:2}}>
                    {Number(viltFallavgift||0)>0&&`${Number(viltFallavgift)} kr fällavgift + `}{Number(viltKottkostnad)*Number(viltVikt)} kr kött
                  </div>
                </div>
              )}
            </div>
          </Dialog>
        );
        return(
          <Dialog open onClose={close} title={title} width={440} actions={<>
            <Button variant="secondary" onClick={close}>Avbryt</Button>
            <Button loading={viltSaving} onClick={async()=>{
              setViltSaving(true);
              try{
                const updated=await api.updateViltDisposition(viltDispModal.reg.id,{disposition:'for_sale',avpalsat:viltAvpalsat,slaktviktKg:viltSlaktvikt?Number(viltSlaktvikt):undefined,trikintestad:viltTrikin,cesiumtestad:viltCesium,saleNotes:viltSaleNotes||undefined});
                setViltRegs(p=>p.map(r=>r.id===viltDispModal.reg.id?updated:r));
                setViltDispModal(null);
              }catch{alert('Kunde inte uppdatera.');}finally{setViltSaving(false);}
            }}>Spara annons</Button>
          </>}>
            <div style={{color:T.text,fontWeight:600,fontSize:15,marginBottom:8}}>Uppgifter för annons</div>
            <div style={{display:'grid',gap:12}}>
              <Switch checked={viltAvpalsat} onChange={setViltAvpalsat} label="Avpälsad" style={{justifyContent:'space-between',color:T.text}}/>
              <Field label="Slaktvikt (kg)"><Input type="number" inputMode="decimal" value={viltSlaktvikt} onChange={e=>setViltSlaktvikt(e.target.value)} placeholder="kg"/></Field>
              {viltDispModal.reg.gameType==='Vildsvin'&&(<>
                <Switch checked={viltTrikin} onChange={setViltTrikin} label="Trikintestad" style={{justifyContent:'space-between',color:T.text}}/>
                <Switch checked={viltCesium} onChange={setViltCesium} label="Cesiumtestad" style={{justifyContent:'space-between',color:T.text}}/>
              </>)}
              <Field label="Anteckningar"><Textarea value={viltSaleNotes} onChange={e=>setViltSaleNotes(e.target.value)} placeholder="Ev. ytterligare info…" rows={2}/></Field>
            </div>
          </Dialog>
        );
      })()}
    </div>
  );
}

/** Hanteringsstegen som en rad med numrerade prickar: klara i ok, aktuellt i accent. */
function HandlingSteps({ T, steps, current }: { T: Theme; steps: string[]; current: number }) {
  return (
    <ol aria-label={`Hantering: ${steps[current] ?? ''}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 0, overflowX: 'auto', listStyle: 'none', margin: 0, padding: '2px 0' }}>
      {steps.map((label, i) => {
        const done = i < current; const active = i === current;
        return (
          <React.Fragment key={label}>
            <li aria-current={active ? 'step' : undefined} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
              <span aria-hidden style={{ width: 24, height: 24, borderRadius: '50%', background: done ? T.ok : active ? T.accentFill : T.surface2, color: done ? T.onOk : active ? T.onAccent : T.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                {done ? '✓' : i + 1}
              </span>
              <span style={{ fontSize: 12, color: active ? T.text : T.textMuted, fontWeight: active ? 700 : 500, marginTop: 4, whiteSpace: 'nowrap' }}>{label}</span>
            </li>
            {i < steps.length - 1 && <li aria-hidden style={{ flex: 1, height: 2, background: done ? T.ok : T.border, minWidth: 8, marginTop: 11 }} />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}
